<template>
  <div class="editable-image-card col-12 h-100 common-card">
   
    <div class="bg" style="box-shadow: none;">
      <div class="image-container">
        <img 
          v-if="imageSrc" 
          :src="imageSrc" 
          class="display-image"
          ref="imgRef"
          @load="onImgLoad"
        />
        <div v-else class="no-image">
          <img 
            v-if="defaultImageUrl" 
            :src="defaultImageUrl" 
            class="default-image"
            alt="기본 이미지"
          />
          <div v-else class="no-image-text">
            <p>이미지가 없습니다.</p>
            <p class="hint" v-if="canEditImage">설정 아이콘을 클릭하여 이미지를 업로드하세요.</p>
          </div>
        </div>
        
        <!-- 컨텍스트 메뉴 -->
        <div 
          v-if="showMenu && canEditImage" 
          class="context-menu"
          :style="menuStyle"
          @click.stop
        >
          <div class="menu-item" @click="openFilePicker">
            <span>이미지 변경</span>
          </div>
          <div class="menu-item" @click="openSiteTitlePopup">
            <span>시스템 타이틀 변경</span>
          </div>
          <div class="menu-item" @click="removeImage" v-if="hasSiteLogo">
            <span>이미지 삭제</span>
          </div>
        </div>
        
        <!-- 숨겨진 파일 입력 -->
        <input 
          ref="fileInputRef" 
          type="file" 
          accept="image/*" 
          style="display: none;"
          @change="handleFileSelect"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import Swal from 'sweetalert2';
import { useEquipStore } from '~/store/equipStore';
import { useTocStore } from '~/store/tocStore';
import { useUserStore } from '~/store/userStore';
import { UserAuthGroup } from '~/enums';
import geoGif from '@/assets/images/renewal/drgeo_default.jpg';

const equipStore = useEquipStore();
const tocStore = useTocStore();
const userStore = useUserStore();

const props = defineProps<{
  apiData?: any;
  property?: {
    title?: string;
    imageUrl?: string;
    uploadApiUrl?: string;
  };
  showMenu?: boolean;  // 외부에서 메뉴 표시 제어
  menuPosition?: { top: number; left: number };  // 메뉴 위치
}>();

const emit = defineEmits<{
  closeMenu: [];
}>();

console.log(tocStore.selectSiteInfo);

const title = computed(() => props.property?.title || '이미지');
const imageSrc = ref<string>('');
const showMenu = ref(false);
const menuStyle = ref<Record<string, string>>({});
const fileInputRef = ref<HTMLInputElement | null>(null);
const imgRef = ref<HTMLImageElement | null>(null);
const isImageDeleted = ref(false); // 이미지 삭제 상태 추적
const siteInfo = computed(() => tocStore.selectSiteInfo);
const siteTitle = ref<string>('');
const isSaving = ref(false);

const currentSiteId = computed(() => {
  const s: any = tocStore.selectSiteInfo as any;
  return s?.siteId ?? null as string | null;
});

const isDrgeoSite = computed(() => {
  const kind = (tocStore.selectSiteInfo as any)?.engSourceKind;
  return kind === 'drgeo';
});

let closeMenuHandler: ((e: MouseEvent) => void) | null = null; // 메뉴 외부 클릭 핸들러

// 권한 체크 computed
const canEditImage = computed(() => {
  const authGroupSeq = userStore.loginUserInfo?.authGroupSeq;
  return authGroupSeq && [UserAuthGroup.Admin, UserAuthGroup.Contractor, UserAuthGroup.RtuManufact, UserAuthGroup.SafetyManager, UserAuthGroup.HostBizOperator].includes(authGroupSeq);
});

const canEditForThisSite = computed(() => canEditImage.value && isDrgeoSite.value);

function getSitePersonalizationFromStore(siteId: string | null) {
  if (!siteId) return { siteSysTitle: null as string | null, sitelogoSeq: null as number | null, attachPath: null as string | null, attachName: null as string | null, enabled: null as any };
  const cached = (tocStore.sitePersonalizationCache as any)?.value?.[siteId] as any;
  const sel: any = tocStore.selectSiteInfo as any;
  const siteSysTitle = (cached?.siteSysTitle ?? sel?.siteSysTitle ?? null) as string | null;
  const sitelogoSeq = (cached?.sitelogoSeq ?? sel?.sitelogoSeq ?? null) as number | null;
  const attachPath = (cached?.attachPath ?? sel?.attachPath ?? null) as string | null;
  const attachName = (cached?.attachName ?? sel?.attachName ?? null) as string | null;
  const enabled = (cached?.enabled ?? sel?.personalizationEnabled ?? sel?.enabled ?? sel?.useYn ?? null) as any;
  return { siteSysTitle, sitelogoSeq, attachPath, attachName, enabled };
}

function buildFilePath(attachPath: string | null, attachName: string | null) {
  if (!attachPath || !attachName) return null;
  // attachPath는 폴더 경로, attachName은 파일명
  return `${attachPath}/${attachName}`.replace(/\\/g, '/');
}

async function loadSiteLogoByAttach(attachPath: string | null, attachName: string | null) {
  // 기존 blob 정리
  if (imageSrc.value && imageSrc.value.startsWith('blob:')) {
    URL.revokeObjectURL(imageSrc.value);
  }

  const filePath = buildFilePath(attachPath, attachName);
  if (!filePath) {
    imageSrc.value = '';
    return;
  }

  const file = await tocStore.getUserTocSettingImage(filePath);
  if (file) {
    imageSrc.value = window.URL.createObjectURL(file);
    isImageDeleted.value = false;
  }
}

const hasSiteLogo = computed(() => {
  const { sitelogoSeq, attachPath, attachName } = getSitePersonalizationFromStore(currentSiteId.value);
  return !!(sitelogoSeq && attachPath && attachName);
});

// 이미지 URL 추출 함수
function extractImageUrl(data: any): string | null {
  if (!data) return null;
  
  // 문자열인 경우
  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed || null;
  }
  
  // 객체인 경우 - 다양한 필드명 확인
  if (typeof data === 'object') {
    // Blob 객체인 경우
    if (data instanceof Blob) {
      return URL.createObjectURL(data);
    }
    
    // Array인 경우 (BaseComponent가 배열로 반환할 수 있음)
    if (Array.isArray(data) && data.length > 0) {
      // 첫 번째 요소에서 이미지 URL 추출 시도
      const firstItem = data[0];
      return extractImageUrl(firstItem);
    }
    
    // 일반 객체인 경우 - 여러 가능한 필드명 확인
    return data.imageUrl || data.image || data.url || data.fileName || data.schematicImage || data.data || null;
  }
  
  return null;
}

// 기본 이미지 URL (이미지가 없을 때 사용)
const defaultImageUrl = computed(() => {
  // property에 기본 이미지가 지정되어 있으면 사용
  if (props.property?.imageUrl) {
    return props.property.imageUrl;
  }
  // 지열 설비의 경우 기본 geo.gif 사용
  return geoGif;
});

// tocStore에서 저장된 이미지 조회
const getStoredImage = (cid: string | null): string | null => {
  if (!cid) return null;
  return tocStore.getSchematicImage(String(cid));
};

// API 데이터에서 이미지 URL 가져오기 (BaseComponent를 통해 전달된 데이터)
watch(() => props.apiData, (newData) => {
  console.log('EditableImageCard apiData changed:', newData);
  const extractedUrl = extractImageUrl(newData);
  if (extractedUrl) {
    // 기존 Blob URL이 있으면 정리
    if (imageSrc.value && imageSrc.value.startsWith('blob:')) {
      URL.revokeObjectURL(imageSrc.value);
    }
    imageSrc.value = extractedUrl;
    isImageDeleted.value = false; // API에서 이미지가 오면 삭제 상태 해제
  } else if (newData === null || newData === undefined) {
    // apiData가 null/undefined인 경우
    // 삭제된 상태가 아니고, imageSrc가 비어있을 때만 tocStore에서 조회
    if (!isImageDeleted.value) {
      const cid = equipStore.selectEquipInfo?.cid;
      const storedImage = getStoredImage(cid);
      if (storedImage) {
        imageSrc.value = storedImage;
      } else if (!imageSrc.value) {
        // 저장된 이미지도 없으면 기본 이미지 사용
        imageSrc.value = defaultImageUrl.value || '';
      }
    }
  }
}, { immediate: true, deep: true });

// 사이트 변경 시 사이트 개인화(타이틀/로고) 로드
watch(
  () => currentSiteId.value,
  async (siteId) => {
    const { siteSysTitle, attachPath, attachName } = getSitePersonalizationFromStore(siteId);
    siteTitle.value = (siteSysTitle ?? '').toString();

    // drgeo 사이트일 때만 사이트 로고 적용(그 외에는 기존 로직 유지)
    if (isDrgeoSite.value) {
      await loadSiteLogoByAttach(attachPath, attachName);
    }
  },
  { immediate: true }
);

// 설비 변경 시 저장된 이미지 조회
watch(() => equipStore.selectEquipInfo?.cid, (newCid, oldCid) => {
  // 설비가 변경된 경우에만 조회 (삭제 상태가 아니고, imageSrc가 비어있을 때)
  if (newCid && newCid !== oldCid && !imageSrc.value && !isImageDeleted.value) {
    const storedImage = getStoredImage(String(newCid));
    if (storedImage) {
      imageSrc.value = storedImage;
      isImageDeleted.value = false; // 새 설비의 이미지 로드 시 삭제 상태 해제
    }
  }
}, { immediate: true });

// property에서 이미지 URL 가져오기 (초기값 또는 기본값)
watch(() => props.property?.imageUrl, (newUrl) => {
  if (newUrl && !imageSrc.value) {
    // apiData가 없을 때만 property의 imageUrl 사용
    imageSrc.value = newUrl;
  }
}, { immediate: true });

// 외부에서 전달된 showMenu prop 감시
watch(() => props.showMenu, (newValue) => {
  if (newValue && canEditImage.value) {
    showMenu.value = true;
    if (props.menuPosition) {
      menuStyle.value = {
        position: 'fixed',
        left: `${props.menuPosition.left}px`,
        top: `${props.menuPosition.top}px`,
        zIndex: '1000'
      };
    }
    
    // 메뉴 외부 클릭 시 닫기 (기존 핸들러 제거 후 새로 추가)
    if (closeMenuHandler) {
      document.removeEventListener('click', closeMenuHandler);
    }
    
    nextTick(() => {
      closeMenuHandler = (e: MouseEvent) => {
        if (!(e.target as HTMLElement).closest('.context-menu')) {
          showMenu.value = false;
          emit('closeMenu');
          if (closeMenuHandler) {
            document.removeEventListener('click', closeMenuHandler);
            closeMenuHandler = null;
          }
        }
      };
      setTimeout(() => {
        document.addEventListener('click', closeMenuHandler!);
      }, 100);
    });
  } else {
    showMenu.value = false;
    if (closeMenuHandler) {
      document.removeEventListener('click', closeMenuHandler);
      closeMenuHandler = null;
    }
  }
});

// 메뉴 상태 변경 감시
watch(showMenu, (newValue) => {
  if (!newValue) {
    emit('closeMenu');
    if (closeMenuHandler) {
      document.removeEventListener('click', closeMenuHandler);
      closeMenuHandler = null;
    }
  }
});

// 파일 선택기 열기
function openFilePicker() {
  if (!canEditForThisSite.value) {
    Swal.fire({
      icon: 'warning',
      title: '권한 없음',
      text: '이미지 변경 권한이 없거나, 대상 사이트가 아닙니다.',
      confirmButtonColor: "#007CE1",
      confirmButtonText: "확인",
    });
    return;
  }
  if (isSaving.value) return;
  showMenu.value = false;
  emit('closeMenu');
  if (fileInputRef.value) {
    fileInputRef.value.click();
  }
}

async function openSiteTitlePopup() {
  if (!canEditForThisSite.value) {
    Swal.fire({
      icon: 'warning',
      title: '권한 없음',
      text: '타이틀 변경 권한이 없거나, 대상 사이트가 아닙니다.',
      confirmButtonColor: "#007CE1",
      confirmButtonText: "확인",
    });
    return;
  }
  if (isSaving.value) return;

  showMenu.value = false;
  emit('closeMenu');

  const result = await Swal.fire({
    title: '시스템 타이틀 변경',
    input: 'text',
    inputValue: siteTitle.value ?? '',
    inputAttributes: {
      maxlength: '25',
      autocapitalize: 'off',
      autocorrect: 'off',
    },
    inputPlaceholder: '25자 이하로 입력하세요.',
    showCancelButton: true,
    confirmButtonColor: "#007CE1",
    cancelButtonColor: "#d33",
    confirmButtonText: "저장",
    cancelButtonText: "취소",
    preConfirm: (value) => {
      const v = (value ?? '').toString().trim();
      if (v.length > 25) {
        Swal.showValidationMessage('시스템 타이틀은 25자 이하로 입력해주세요.');
        return false as any;
      }
      return v;
    }
  });

  if (!result.isConfirmed) return;
  const newTitle = ((result.value ?? '') as string).trim();
  const siteId = currentSiteId.value;
  if (!siteId) return;

  try {
    isSaving.value = true;
    await tocStore.setSitePersonalization(siteId, newTitle || undefined, undefined);
    siteTitle.value = newTitle;
    Swal.fire({
      icon: 'success',
      title: '저장 완료',
      text: '시스템 타이틀이 저장되었습니다.',
      confirmButtonColor: "#007CE1",
      confirmButtonText: "확인",
    });
  } finally {
    isSaving.value = false;
  }
}

// 파일 선택 처리
async function handleFileSelect(event: Event) {
  const target = event.target as HTMLInputElement;
  if (!target.files || target.files.length === 0) return;
  
  const file = target.files[0];
  
  // 파일 크기 검증 (10MB 제한)
  const maxSize = 10 * 1024 * 1024; // 10MB
  if (file.size > maxSize) {
    Swal.fire({
      icon: 'error',
      title: '파일 크기 초과',
      text: '이미지 파일은 10MB 이하여야 합니다.',
      confirmButtonColor: "#007CE1",
      confirmButtonText: "확인",
    });
    return;
  }
  
  // 파일 타입 검증
  if (!file.type.startsWith('image/')) {
    Swal.fire({
      icon: 'error',
      title: '잘못된 파일 형식',
      text: '이미지 파일만 업로드할 수 있습니다.',
      confirmButtonColor: "#007CE1",
      confirmButtonText: "확인",
    });
    return;
  }
  
  // 이미지 미리보기 및 tocStore에 저장
  const reader = new FileReader();
  reader.onload = (e) => {
    if (e.target?.result) {
      const dataUrl = e.target.result as string;
      imageSrc.value = dataUrl;
      
      // 미리보기(즉시 반영)
      isImageDeleted.value = false;
    }
  };
  reader.readAsDataURL(file);

  // 사이트 개인화 저장(API)
  const siteId = currentSiteId.value;
  if (siteId && isDrgeoSite.value) {
    try {
      isSaving.value = true;
      await tocStore.setSitePersonalization(siteId, siteTitle.value || undefined, file);
      Swal.fire({
        icon: 'success',
        title: '저장 완료',
        text: '이미지가 저장되었습니다.',
        confirmButtonColor: "#007CE1",
        confirmButtonText: "확인",
      });
    } finally {
      isSaving.value = false;
    }
  }
  
  // input 초기화
  if (target) {
    target.value = '';
  }
}

// TODO: API 개발 완료 후 주석 해제
// // 이미지 업로드 (API 사용)
// async function uploadImage(file: File) {
//   const uploadUrl = props.property?.uploadApiUrl || '/api/v1/equips/{cid}/schematic-image/upload';
//   const cid = equipStore.selectEquipInfo?.cid;
//   
//   if (!cid) {
//     throw new Error('설비 정보가 없습니다.');
//   }
//   
//   const url = uploadUrl.replace('{cid}', String(cid));
//   
//   const formData = new FormData();
//   formData.append('file', file);
//   formData.append('cid', String(cid));
//   
//   const response = await $fetch(url, {
//     method: 'POST',
//     body: formData,
//   });
//   
//   if (response && response.imageUrl) {
//     imageSrc.value = response.imageUrl;
//   }
//   
//   return response;
// }

// 이미지 삭제
function removeImage() {
  if (!canEditForThisSite.value) {
    Swal.fire({
      icon: 'warning',
      title: '권한 없음',
      text: '이미지 삭제 권한이 없거나, 대상 사이트가 아닙니다.',
      confirmButtonColor: "#007CE1",
      confirmButtonText: "확인",
    });
    return;
  }
  if (isSaving.value) return;
  
  showMenu.value = false;
  emit('closeMenu');
  
  Swal.fire({
    title: '이미지 삭제',
    text: '이미지를 삭제하시겠습니까?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: "#007CE1",
    cancelButtonColor: "#d33",
    confirmButtonText: "삭제",
    cancelButtonText: "취소"
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        const siteId = currentSiteId.value;
        if (siteId && isDrgeoSite.value) {
          isSaving.value = true;
          await tocStore.removeSitePersonalization(siteId);
        }
        
        // 기존 Blob URL이 있으면 정리
        if (imageSrc.value && imageSrc.value.startsWith('blob:')) {
          URL.revokeObjectURL(imageSrc.value);
        }
        
        // 이미지 소스 초기화 및 삭제 상태 설정
        imageSrc.value = '';
        isImageDeleted.value = true; // 삭제 상태로 설정하여 watch가 다시 이미지를 로드하지 않도록 함
        
        // TODO: API 개발 완료 후 아래 코드 주석 해제 및 위의 tocStore 삭제 코드 제거
        // // 삭제 API 호출
        // if (props.property?.uploadApiUrl) {
        //   try {
        //     const deleteUrl = props.property.uploadApiUrl.replace('/upload', '/delete');
        //     const cid = equipStore.selectEquipInfo?.cid;
        //     
        //     if (cid) {
        //       await $fetch(deleteUrl.replace('{cid}', String(cid)), {
        //         method: 'DELETE',
        //       });
        //     }
        //   } catch (error) {
        //     console.error('이미지 삭제 실패:', error);
        //   }
        // }
        
        Swal.fire({
          icon: 'success',
          title: '삭제 완료',
          text: '이미지가 삭제되었습니다.',
          confirmButtonColor: "#007CE1",
          confirmButtonText: "확인",
        });
      } catch (error) {
        console.error('이미지 삭제 중 오류 발생:', error);
        Swal.fire({
          icon: 'error',
          title: '삭제 실패',
          text: '이미지 삭제 중 오류가 발생했습니다.',
          confirmButtonColor: "#007CE1",
          confirmButtonText: "확인",
        });
      } finally {
        isSaving.value = false;
      }
    }
  });
}

function onImgLoad() {
  nextTick(() => {
    if (imgRef.value) {
      console.log('이미지 로드 완료');
    }
  });
}

onMounted(() => {
  // 초기 이미지 로드: tocStore에서 저장된 이미지 우선 조회
  const cid = equipStore.selectEquipInfo?.cid;
  if (cid && !imageSrc.value) {
    const storedImage = getStoredImage(String(cid));
    if (storedImage) {
      imageSrc.value = storedImage;
      console.log('tocStore에서 저장된 이미지를 로드했습니다. cid:', cid);
    }
  }
  
  // apiData가 없고 저장된 이미지도 없을 때만 property의 imageUrl 사용
  if (!imageSrc.value && props.property?.imageUrl) {
    imageSrc.value = props.property.imageUrl;
  }
  
  // 디버깅을 위한 로그
  console.log('EditableImageCard mounted:', {
    apiData: props.apiData,
    property: props.property,
    imageSrc: imageSrc.value,
    cid: cid,
    storedImage: cid ? getStoredImage(String(cid)) : null
  });
});

// 컴포넌트 언마운트 시 Blob URL 정리 및 이벤트 리스너 제거
onUnmounted(() => {
  if (imageSrc.value && imageSrc.value.startsWith('blob:')) {
    URL.revokeObjectURL(imageSrc.value);
  }
  if (closeMenuHandler) {
    document.removeEventListener('click', closeMenuHandler);
    closeMenuHandler = null;
  }
});

</script>

<style lang="scss" scoped>
@import "@/assets/styles/design-toc/schematic-card.scss";
@import "@/assets/styles/design-toc/define_font.scss";

.editable-image-card {
  padding: 0 !important;
  width: 100%;
  height: 100%;
  overflow: hidden;
  
  .common-card {
    padding: 0 !important;
    width: 100%;
    height: 100%;
  }
  
  .bg {
    padding: 0 !important;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }
}

.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  cursor: default;
  border-radius: 6px;
  box-sizing: border-box;
}

.display-image {
  display: block;
  width: 100%;
  height: 100%;
  max-width: 100%;
  max-height: 100%;
  object-fit: fill;
  border-radius: 6px;
  user-select: none;
  box-sizing: border-box;
}

.no-image {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: #999;
  
  .default-image {
    display: block;
    width: 100%;
    height: 100%;
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 6px;
    opacity: 0.7;
    box-sizing: border-box;
  }
  
  .no-image-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    
    p {
      margin: 0.5rem 0;
      @include text-normal-14;
      
      &.hint {
        @include text-normal-12;
        color: #ccc;
      }
    }
  }
}

.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  z-index: 1000;
  
  .menu-item {
    padding: 10px 16px;
    cursor: pointer;
    @include text-normal-14;
    transition: background-color 0.2s;
    
    &:hover {
      background-color: #f5f5f5;
    }
    
    &:first-child {
      border-radius: 4px 4px 0 0;
    }
    
    &:last-child {
      border-radius: 0 0 4px 4px;
    }
    
    span {
      display: block;
    }
  }
}

// 반응형 미디어 쿼리
@media (max-width: 768px) {
  .context-menu {
    min-width: 120px;
    
    .menu-item {
      padding: 8px 12px;
      @include text-normal-12;
    }
  }
}
</style>

